/**
 * The app's own host, looked up LAZILY and remembered for the page's
 * lifetime (functional review, 2026-09-24).
 *
 * WHY LAZY. The bridge used to ask `/api/app-host/lookup` on EVERY mount off
 * a host, canvas tiles included, to know whether `install.request` could
 * send the person to the app's own address. Nearly every app has no host,
 * so that was one request per app mount, platform-wide, answering nothing
 * an app had asked for. The host is now resolved only when something needs
 * it: an app calling `host.install.state()` or `host.install.request()`, or
 * the platform's own install affordance. Until then the `__ready__` context
 * says `install.state: 'unknown'`.
 *
 * ONE ANSWER PER CONTENT ID PER PAGE. Hosts change by an operator issuing or
 * revoking one, never within a page's life, so the first answer (a host, or
 * `null` for none) is kept; two apps mounting the same content share it. A
 * FAILED lookup (offline, a 5xx) is not an answer and is forgotten, so the
 * next ask tries again rather than reporting "no host" for the rest of the
 * session.
 *
 * `{ host: null }` with a 200 is the route's word for "no host"; a non-2xx
 * is a failure. Client-safe: no React, no server import.
 */

export interface AppHostLookup {
  host: string;
  origin: string;
  installUrl: string;
  manifestId: string;
  platformZone: boolean;
  /** Present only when the caller owns (or administers) the host. */
  verificationId?: string;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

const cache = new Map<string, Promise<AppHostLookup | null>>();

function parse(body: unknown): AppHostLookup | null {
  const b = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  if (!b || typeof b.host !== 'string' || typeof b.origin !== 'string') return null;
  return {
    host: b.host,
    origin: b.origin,
    installUrl: typeof b.installUrl === 'string' ? b.installUrl : `${b.origin}/`,
    manifestId: typeof b.manifestId === 'string' ? b.manifestId : `${b.origin}/`,
    platformZone: b.platformZone === true,
    ...(typeof b.verificationId === 'string' ? { verificationId: b.verificationId } : {}),
  };
}

/**
 * The host serving `contentId`, or null when it has none. Rejects on a
 * failed request (and forgets it), resolves otherwise, memoised per id.
 */
export function lookupAppHost(
  contentId: string,
  fetchImpl: FetchLike = (input, init) => fetch(input, init),
): Promise<AppHostLookup | null> {
  const key = contentId.trim().toLowerCase();
  const hit = cache.get(key);
  if (hit) return hit;
  const pending = (async () => {
    const res = await fetchImpl(`/api/app-host/lookup?contentId=${encodeURIComponent(key)}`, {
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`app host lookup answered ${res.status}`);
    return parse(await res.json());
  })();
  cache.set(key, pending);
  pending.catch(() => {
    if (cache.get(key) === pending) cache.delete(key);
  });
  return pending;
}

/** Test seam: forget every remembered answer. */
export function resetAppHostLookupCache(): void {
  cache.clear();
}
